import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import MoveCard from './MoveCard';
import CounterArrivalCards, { arrivalTasks } from './CounterArrivalCards';
import { useOrderWorkflow } from './useOrderWorkflow';
import HiddenFold from './HiddenFold';
import { ZoneNumeral } from './Badges';
import { excludedMoves } from './exclusions';
import {
  countedMoves, fixtureLabelOf, monthDay, movesOfFixture, pieces, walkFixtureIds, zoneListText,
} from './format';
import { C, MONO, RADIUS, SHADOW, VERB, hiddenFoldLabel } from './tokens';
import {
  RestockV2ExcludedMove, RestockV2Move, RestockV2Plan, RestockV2ThumbTarget,
} from './types';

interface TodayScreenProps {
  plan: RestockV2Plan;
  moves: RestockV2Move[];
  doneRows: Record<string, boolean>;
  pendingRows: Record<string, boolean>;
  whyRows: Record<string, boolean>;
  openZones: Record<string, boolean>;
  // The first rack with work opens itself; everything else waits for a tap.
  defaultOpenId: string | null;
  // ≥1024 px: a sticky rack rail on the left and 2–3 cards per row. ≥1440 px: three.
  desktop: boolean;
  wide: boolean;
  onToggleZone: (key: string) => void;
  onDone: (move: RestockV2Move) => void;
  onUndo: (move: RestockV2Move) => void;
  // 今天不做 - takes one move out of the walk (and out of the denominator above) for today only.
  onSkip: (move: RestockV2Move) => void;
  skipBusy: (move: RestockV2Move) => boolean;
  // Everything 今天不做 is hiding, straight off the server's plan.excluded.today.
  skipPending: Record<string, boolean>;
  onAddBackMove: (row: RestockV2ExcludedMove) => void;
  onWhy: (id: string) => void;
  onCopy: (text: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const Section: React.FC<{
  id: string; label: string; numeral: React.ReactNode; count: string; open: boolean;
  onToggle: () => void; note?: string; children: React.ReactNode;
}> = ({ id, label, numeral, count, open, onToggle, note, children }) => (
  <Box
    component="section" data-zone={id}
    sx={{
      mb: 1.25, border: '1px solid', borderColor: C.line, borderRadius: `${RADIUS}px`,
      overflow: 'hidden', bgcolor: C.card, boxShadow: SHADOW,
    }}
  >
    <Box
      component="button" type="button" onClick={onToggle} aria-expanded={open}
      aria-label={`${label} · ${count}`}
      sx={{
        width: '100%', appearance: 'none', bgcolor: C.wash, border: 0, cursor: 'pointer',
        borderBottom: open ? '1px solid' : 0, borderColor: C.line,
        display: 'flex', alignItems: 'center', gap: 1, px: 1.4, py: 1.25, textAlign: 'left',
      }}
    >
      {numeral}
      <Typography component="span" sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em' }}>{label}</Typography>
      <Typography
        component="span"
        sx={{ ml: 'auto', fontFamily: MONO, fontSize: 11.5, color: C.ink2, fontWeight: 600 }}
      >
        {count}
      </Typography>
      <Typography
        component="span" aria-hidden="true"
        sx={{ fontSize: 11, color: C.ink3, flex: 'none', transform: open ? 'none' : 'rotate(-90deg)' }}
      >
        ▾
      </Typography>
    </Box>
    <Collapse in={open} unmountOnExit>
      <Box sx={{ p: 1 }}>
        {note && <Typography sx={{ fontSize: 12, color: C.ink2, lineHeight: 1.5, mx: '4px', mb: 1.25 }}>{note}</Typography>}
        {children}
      </Box>
    </Collapse>
  </Box>
);

const CardGrid: React.FC<{ columns: number; children: React.ReactNode }> = ({ columns, children }) => (
  <Box
    sx={{
      display: 'grid', gap: 1,
      ...(columns > 1
        ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
        : { '@media (min-width:600px)': { gridTemplateColumns: '1fr 1fr' } }),
    }}
  >
    {children}
  </Box>
);

// 今天 is the walk: one collapsible section per rack in walk order, the on_arrival 帶回 and the
// 等到貨 receipts quarantined at the bottom so they never inflate 「n / N 件事」.
const TodayScreen: React.FC<TodayScreenProps> = ({
  plan, moves, doneRows, pendingRows, whyRows, openZones, defaultOpenId, desktop, wide,
  onToggleZone, onDone, onUndo, onSkip, skipBusy, skipPending, onAddBackMove, onWhy, onCopy, onImage,
}) => {
  const workflow = useOrderWorkflow();
  const arrivals = arrivalTasks(workflow.tasks);
  const tracked = new Set(workflow.tasks.filter(t => !t.legacy_receipt &&
    t.pending_office_qty + t.pending_transit_qty + t.pending_display_qty > 0).map(t => String(t.variant_id)));
  // A tracked parcel has its own quantity-specific task. Never offer a second stock move.
  moves = moves.filter(move => !tracked.has(String(move.line.variant_id)));
  const walk = walkFixtureIds(plan);
  const todo = countedMoves(moves);
  const doneCount = todo.filter((move) => doneRows[move.id]).length;
  const leftPieces = pieces(todo.filter((move) => !doneRows[move.id]).map((move) => move.line));
  const arrival = monthDay(plan.meta?.expected_arrival);
  const later = moves.filter((move) => move.kind === 'on_arrival');
  const waiting = moves.filter((move) => move.kind === 'waiting');
  const incoming = plan.meta?.fit?.incoming_garments ?? 0;
  const skipped = excludedMoves(plan);

  const columns = wide ? 3 : (desktop ? 2 : 1);
  const jump = (id: string) => {
    if (openZones[id] === undefined ? id !== defaultOpenId : !openZones[id]) onToggleZone(id);
    const node = document.querySelector(`[data-zone="${id}"]`);
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'start' });
  };

  const renderCards = (rows: RestockV2Move[]) => (
    <CardGrid columns={columns}>
      {rows.map((move) => (
        <MoveCard
          key={move.id} move={move} done={!!doneRows[move.id]} pending={!!pendingRows[move.id]}
          whyOpen={!!whyRows[move.id]} arrivalLabel={arrival} skipPending={skipBusy(move)}
          onDone={onDone} onUndo={onUndo} onSkip={onSkip} onWhy={onWhy} onImage={onImage}
        />
      ))}
    </CardGrid>
  );

  return (
    <Box>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: C.paper, pb: 1.25 }}>
        <Box
          sx={{
            bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: `${RADIUS}px`,
            boxShadow: SHADOW, px: 1.4, py: 1.2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.9 }}>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, letterSpacing: '-.03em' }}>
              {doneCount}
            </Typography>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: 13, color: C.ink3 }}>
              / {todo.length} 件事
            </Typography>
            <Typography component="span" sx={{ ml: 'auto', fontSize: 11.5, color: C.ink2, fontWeight: 700 }}>
              {doneCount >= todo.length && todo.length > 0 ? '全部做完了 🎉' : `衣服還有 ${leftPieces} 件`}
            </Typography>
          </Box>
          <Box sx={{ height: 7, bgcolor: C.paper2, borderRadius: '99px', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%', bgcolor: C.go, transition: 'width .25s',
                width: `${todo.length ? Math.round((doneCount / todo.length) * 100) : 0}%`,
              }}
            />
          </Box>
        </Box>
      </Box>

      {arrivals.length > 0 && (
        <Section
          id="__arrivals" label="門市訂單到貨"
          numeral={<ZoneNumeral mark="到" bg={C.go} size={11} />}
          count={`${arrivals.length} 款`}
          open={openZones.__arrivals === undefined ? true : openZones.__arrivals}
          onToggle={() => onToggleZone('__arrivals')}
          note="出貨人員已經收到這些貨；拿到門市、掛上架之後按一下「收到了」就行了。"
        >
          <CounterArrivalCards workflow={workflow} columns={columns} />
        </Section>
      )}

      {todo.length === 0 && arrivals.length === 0 && (
        <Typography sx={{ fontSize: 13, color: C.ink2, mb: 1.5 }}>
          今天沒有待辦。
        </Typography>
      )}

      <Box
        sx={desktop
          ? { display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 2, alignItems: 'start' }
          : undefined}
      >
        {desktop && (
          <Box sx={{ position: 'sticky', top: 96 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: C.ink3, mb: 1 }}>
              走一圈的順序
            </Typography>
            {walk.map((id, index) => {
              const rows = movesOfFixture(moves, id, true);
              const done = rows.filter((move) => doneRows[move.id]).length;
              return (
                <Box
                  key={id} component="button" type="button" onClick={() => jump(id)}
                  aria-label={`跳到 ${fixtureLabelOf(plan, id)}`}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', textAlign: 'left',
                    cursor: 'pointer', mb: 0.5, px: 1, py: 0.9, borderRadius: '9px',
                    border: '1px solid', borderColor: C.line,
                    bgcolor: rows.length ? C.card : C.wash,
                    color: rows.length ? C.ink : C.ink3,
                  }}
                >
                  <ZoneNumeral index={index} size={11} />
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, minWidth: 0 }}>
                    {fixtureLabelOf(plan, id)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ ml: 'auto', fontFamily: MONO, fontSize: 12, color: C.ink2, flex: 'none' }}
                  >
                    {rows.length ? `${done} / ${rows.length}` : '—'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
      {walk.map((id, index) => {
        const rows = movesOfFixture(moves, id, true);
        const label = fixtureLabelOf(plan, id);
        const done = rows.filter((move) => doneRows[move.id]).length;
        return (
          <Section
            key={id} id={id} label={label} numeral={<ZoneNumeral index={index} size={11} />}
            count={rows.length ? `${done} / ${rows.length}` : '不用動'}
            open={openZones[id] === undefined ? id === defaultOpenId : openZones[id]}
            onToggle={() => onToggleZone(id)}
          >
            {rows.length ? (
              <>
                {renderCards(rows)}
                <Box
                  component="button" type="button" onClick={() => onCopy(zoneListText(label, rows))}
                  aria-label={`複製${label}清單`}
                  sx={{
                    mt: 1, width: '100%', py: 1, cursor: 'pointer', border: '1px dashed',
                    borderColor: C.line2, bgcolor: C.wash, borderRadius: '9px', fontSize: 12.5,
                    fontWeight: 700, color: C.ink2,
                  }}
                >
                  複製這個架子的清單
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: C.ink2, textAlign: 'center', py: 1.5 }}>
                這個架子今天不用動，直接走下一個。
              </Typography>
            )}
          </Section>
        );
      })}

        </Box>
      </Box>

      {later.length > 0 && (
        <Section
          id="__on_arrival" label={`${arrival} 到貨後才帶回`}
          numeral={<ZoneNumeral mark={VERB.on_arrival.mark} bg={VERB.on_arrival.colour} size={11} />}
          count={`${pieces(later.map((move) => move.line))} 件`}
          open={openZones.__on_arrival === true}
          onToggle={() => onToggleZone('__on_arrival')}
          note={`新貨 ${incoming} 件到了才有位子，這些今天先掛著不用動，也不算在上面的件數裡。`}
        >
          {renderCards(later)}
        </Section>
      )}

      {/* 「今天略過 N 件 · 查看」: the walk never drops a move silently, and 加回 is one tap. */}
      <HiddenFold
        label={hiddenFoldLabel('today', skipped.length)} moves={skipped} pending={skipPending}
        onAddBackMove={onAddBackMove}
      />

      {waiting.length > 0 && (
        <Section
          id="__waiting" label="等到貨"
          numeral={<ZoneNumeral mark={VERB.waiting.mark} bg={VERB.waiting.colour} size={11} />}
          count={`${pieces(waiting.map((move) => move.line))} 件`}
          open={openZones.__waiting === true}
          onToggle={() => onToggleZone('__waiting')}
          note="門市訂單由收貨頁點收後，接續上方的移轉與上架待辦。舊版訂貨紀錄請確認實際入庫後再結案。"
        >
          {renderCards(waiting)}
        </Section>
      )}
    </Box>
  );
};

export default TodayScreen;
